import api from '../lib/api';
import type { OnboardingStep } from '../components/Pleri/components/ChatBox/OnboardingProgressSteps';

// API Response Types
export interface OnboardingStepAction {
  prompt?: string;
  url?: string;
  newWindow?: boolean;
}

export interface OnboardingStepResponse {
  id: string;
  label: string;
  description: string;
  order: number;
  status: 'completed' | 'pending' | 'dismissed';
  action: OnboardingStepAction;
  category: string;
  isDismissible?: boolean;
}

export interface OnboardingStepsResponse {
  data: {
    items: OnboardingStepResponse[];
  };
}

/**
 * Dismiss an onboarding step
 * @param itemId The ID of the onboarding item to dismiss
 */
export const dismissOnboardingStep = async (itemId: string): Promise<void> => {
  try {
    await api.put(`/api/core/pleri/onboarding/${itemId}/dismiss`);
  } catch (error) {
    console.error('Failed to dismiss onboarding step:', error);
    throw error;
  }
};

/**
 * Fetch onboarding steps from the API
 * @returns Array of onboarding steps mapped to OnboardingStep format
 */
export const getOnboardingSteps = async (): Promise<OnboardingStep[]> => {
  try {
    const response = await api.get<OnboardingStepsResponse>(
      '/api/core/pleri/onboarding',
    );

    const steps = response.data.data.items;

    // Sort by order
    const sortedSteps = [...steps].sort((a, b) => a.order - b.order);

    // Find the first pending step to mark as 'current'
    const firstPendingIndex = sortedSteps.findIndex(
      (step) => step.status === 'pending',
    );

    // Map to OnboardingStep format
    return sortedSteps.map((step, index) => {
      // Treat dismissed as completed
      let status: OnboardingStep['status'] =
        step.status === 'dismissed' ? 'completed' : step.status;

      // Mark the first pending step as 'current'
      if (step.status === 'pending' && index === firstPendingIndex) {
        status = 'current';
      }

      return {
        id: step.id,
        label: step.label,
        status,
        action: step.action,
        isDismissible: step.isDismissible,
      };
    });
  } catch (error) {
    console.error('Failed to fetch onboarding steps:', error);
    // Return empty array on error
    return [];
  }
};
